"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, Building2, FileCheck2, Scale, Calculator, ArrowRight, ShieldCheck, Briefcase } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function Team() {
  const shouldReduceMotion = useReducedMotion();
  const areas = [{
    title: "Especialistas contables y fiscales",
    discipline: "Empresa y Dictámenes",
    icon: Calculator,
    description: "Auditoría preventiva, cálculo estratégico de contribuciones y reestructuración tributaria con apego estricto a las disposiciones fiscales vigentes.",
    highlights: ["Defensa y cumplimiento ante el SAT", "Diagnóstico y regularización fiscal", "Control interno y supervisión financiera"],
    linkText: "Consultar servicios contables",
    to: "/CompaniesPage"
  }, {
    title: "Gestión gubernamental y finanzas públicas",
    discipline: "Sector Público",
    icon: Building2,
    description: "Asesoría técnica en armonización contable CONAC, fiscalización institucional, solventación de pliegos y acompañamiento en licitaciones públicas.",
    highlights: ["Armonización contable gubernamental", "Solventación de observaciones de auditoría", "Supervisión de procesos de licitación"],
    linkText: "Ver asesoría gubernamental",
    to: "/GovernmentPage"
  }, {
    title: "Abogados corporativos y administrativos",
    discipline: "Jurídico y Litigio",
    icon: Scale,
    description: "Patrocinio legal en controversias administrativas, blindaje de convenios comerciales, cumplimiento normativo y asesoramiento agrario especializado.",
    highlights: ["Litigio administrativo y fiscal", "Contratación mercantil y societaria", "Regularización de derechos agrarios"],
    linkText: "Explorar división jurídica",
    to: "/LegalPage"
  }];
  const credentials = [{
    label: "FORMACIÓN PROFESIONAL",
    value: "*Contador Público *Licenciado en Derecho\n\xA0*Experiencia en gestión pública y empresarial"
  }, {
    label: "DIRECCIÓN Y ESTRATEGIA",
    value: "*Liderazgo de equipo multidisciplinario *Planeación y cumplimiento normativo\n*Soluciones integrales a la medida"
  }, {
    label: "RESULTADOS QUE GENERAN VALOR",
    value: "*Transparencia y control\n*Eficiencia en la gestión de recursos *Acompañamiento técnico y legal en cada proyecto"
  }];
  return <section id="equipo-profesional" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-ring/40 bg-accent/40 text-foreground text-xs font-semibold uppercase tracking-widest mb-4">
            <span>Gobierno</span>
            <span className="text-ring font-bold">·</span>
            <span>Empresa</span>
            <span className="text-ring font-bold">·</span>
            <span>Legal</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-wide uppercase leading-tight text-foreground">
            Equipo profesional y directivo
          </h2>
          <div className="w-12 h-[2px] bg-ring mt-4 mb-5" />
          <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed">
            Una estructura directiva coordinada donde cada área técnica es conducida por profesionales certificados, garantizando dictámenes solventes y respaldo institucional inmediato.
          </p>
        </div>

        {/* Executive Director Profile Card */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mb-16 rounded-md border border-border bg-card p-6 md:p-10 lg:p-12 shadow-sm relative overflow-hidden">
          {/* Subtle gold top hairline accent */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-ring" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Director Photo Frame */}
            <div className="lg:col-span-5 flex flex-col items-center sm:items-start">
              <div className="relative w-full max-w-sm rounded-sm border border-border/80 p-2 bg-background shadow-xs">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_director_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_director_portrait.png-wvc-sizes" alt="Retrato formal de L.C.P. Jesús Adán Avilez Soto, Director General de ASV Consultores" loading="lazy" decoding="async" className="w-full aspect-4/5 object-cover rounded-xs" src="https://asvconsultores.com/wp-content/uploads/2026/09/soyadansoto.jpg" />
                <div className="absolute -bottom-3 right-5 bg-primary text-primary-foreground border border-ring/40 px-3 py-1.5 rounded-xs flex items-center gap-2 shadow-sm text-xs font-serif uppercase tracking-wider">
                  <ShieldCheck className="size-3.5 text-ring" />
                  <span>Dirección General</span>
                </div>
              </div>
            </div>

            {/* Director Bio & Institutional Role */}
            <div className="lg:col-span-7 flex flex-col justify-between h-full">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  <Briefcase className="size-3.5 text-ring" />
                  <span>Liderazgo institucional</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold uppercase tracking-normal text-foreground mb-1">
                  L.C.P. Jesús Adán Avilez Soto
                </h3>
                <p className="text-sm md:text-base font-semibold text-ring uppercase tracking-wide mb-6">CONTADOR PÚBLICO · ABOGADO · DIRECTOR GENERAL</p>

                <p className="text-muted-foreground font-sans text-sm md:text-base leading-relaxed mb-6">Profesional con amplia experiencia en la gestión pública, fiscal, legal y empresarial, liderando el equipo multidisciplinario de ASV Consultores para ofrecer soluciones integradas y estratégicas.<div><br /></div><div>Mi compromiso es brindar un servicio con rigor técnico, apego normativo y resultados medibles, acompañando a gobiernos, empresas y particulares en cada proyecto con ética, responsabilidad y visión de largo plazo.</div></p>

                <Separator className="my-6 bg-border" />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  {credentials.map((cred, idx) => <div key={idx} data-index={idx} className="space-y-1">
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                        {cred.label}
                      </p>
                      <p className="text-xs sm:text-sm font-medium text-foreground leading-snug">
                        {cred.value}
                      </p>
                    </div>)}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild className="rounded-xs uppercase font-serif tracking-wider text-xs px-6 py-2.5 bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ContactPage">
                    Solicitar reunión directiva
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xs uppercase font-serif tracking-wider text-xs px-6 py-2.5 border-primary/30 text-foreground hover:bg-accent/40">
                  <Link to="/OnlineConsultingPage">
                    Consultoría en línea
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section divider with thematic text */}
        <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-border pb-4">
          <div>
            <span className="text-xs uppercase font-bold tracking-widest text-muted-foreground">
              Rigor técnico colegiado
            </span>
            <h3 className="font-serif text-xl md:text-2xl font-semibold uppercase tracking-wide text-foreground mt-1">
              Especialistas por área disciplinaria
            </h3>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground max-w-md">
            Atención conducida por titulares con posgrados y certificación profesional en cada vertiente de práctica.
          </p>
        </div>

        {/* 3-Column Multidisciplinary Leadership Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {areas.map((area, index) => {
          const IconComponent = area.icon;
          return <motion.div key={area.title} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.45,
            delay: index * 0.08,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between rounded-xs border-border bg-card shadow-xs hover:border-ring/50 transition-[border-color,box-shadow] duration-200">
                  <div className="h-[2px] w-full bg-ring/60" />
                  <CardHeader className="pt-6 pb-2">
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-11 rounded-xs border border-border bg-background flex items-center justify-center text-foreground">
                        <IconComponent className="size-5 text-ring" />
                      </div>
                      <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground px-2 py-0.5 rounded-xs border border-border bg-background">
                        {area.discipline}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-lg font-semibold uppercase tracking-wide leading-snug text-foreground">
                      {area.title}
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground font-sans mt-2 leading-relaxed">
                      {area.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="py-4 space-y-3">
                    <p className="text-xs uppercase tracking-wider font-semibold text-foreground/80">
                      Capacidades operativas:
                    </p>
                    <ul className="space-y-2">
                      {area.highlights.map((item, itemIdx) => <li key={itemIdx} data-index={itemIdx} className="flex items-start gap-2 text-xs text-muted-foreground leading-normal">
                          <FileCheck2 className="size-3.5 text-ring shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-2 pb-6 border-t border-border/60 mt-auto">
                    <Button asChild variant="link" className="p-0 h-auto text-xs font-semibold uppercase tracking-wider text-foreground hover:text-ring flex items-center gap-1.5 transition-colors">
                      <Link to={area.to}>
                        <span>{area.linkText}</span>
                        <ArrowRight className="size-3.5 text-ring" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Operational Commitment Banner */}
        <div className="mt-14 rounded-xs border border-border bg-muted/40 p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="font-serif text-base md:text-lg font-semibold uppercase tracking-wide text-foreground">
              Modelo de coordinación multidisciplinaria
            </h4>
            <p className="text-xs md:text-sm text-muted-foreground max-w-2xl">
              Cada expediente cuenta con un titular responsable y la supervisión de la Dirección General para respaldar dictámenes solventes y respuesta oportuna.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            <Button asChild variant="outline" className="rounded-xs uppercase font-serif text-xs px-5 py-2 border-border hover:bg-background text-foreground">
              <Link to="/ContactPage">
                Hablar con un especialista
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}